import { useState } from "react";
import { endpoints } from "../api/endpoints";
import { useApi, useFreshness } from "../api/hooks";
import { reasonMessage } from "../api/problem";
import type { HeldItem, InspectionOutcome } from "../api/types";
import { can, holdsWard, ROLE } from "../auth/roles";
import { ActionDialog } from "../components/ActionDialog";
import { StatusBadge } from "../components/Badges";
import { FileLinks } from "../components/Files";
import {
  ANSWER_RESULT,
  AUTH_KIND,
  DEFECT_SEVERITY,
  FIELD_OUTCOME,
  HELD_KIND,
  HELD_STATUS,
  HOLD_REASON,
  HYDRANT_CONDITION,
  INSPECTION_KIND,
  INSPECTION_OUTCOME,
  labelOf,
  statusOf,
} from "../components/labels";
import { Notice, QueryState, StaleNotice } from "../components/ProblemAlert";
import { Tabs } from "../components/Tabs";
import { useFireContext } from "../context";
import { formatDateTime, shortId } from "../format/datetime";
import { checklistItem, ItemStandard, metres, personLabel, reasonError, Row, ScreenHeader, useConfigNames, useWardNames } from "./shared";
import { Button, IconButton, IconClose } from "@lcc/ui";

function InspectionDetail({ inspectionId, onClose }: { inspectionId: string; onClose?: () => void }) {
  const { principal } = useFireContext();
  const names = useConfigNames();
  const wardName = useWardNames();
  const q = useApi(["inspection", inspectionId], (api) => endpoints.getInspection(api, inspectionId));
  const i = q.data;
  const fresh = useFreshness(q);
  if (!i) return <QueryState isLoading={q.isLoading} error={q.error} label="the inspection" />;
  const checklist = names.staff?.checklists.find((c) => c.key === i.checklist_key);
  const itemOf = (code: string) => checklistItem(names.staff, code, i.checklist_key);
  const itemLabel = (code: string) => itemOf(code)?.label ?? code;
  const critical = (code: string) => checklist?.items.find((x) => x.code === code)?.critical === true;
  return (
    <section className="fer-panel fer-record" aria-label="Inspection">
      <div className="lcc-detail__head">
        <div className="lcc-detail__titles">
          <h3 className="lcc-detail__title">{labelOf(INSPECTION_KIND, i.kind)} of {formatDateTime(i.captured_at)}</h3>
          <span className="fer-badges fer-record__status">
            <StatusBadge status={statusOf(INSPECTION_OUTCOME, i.outcome)} />
          </span>
        </div>
        {onClose && <IconButton label="Close" size="sm" icon={<IconClose size={16} />} onClick={onClose} />}
      </div>
      <StaleNotice {...fresh} error={q.error} what="inspection" />
      <dl className="fer-dl">
        <Row term="Premises">{shortId(i.premises_id)}</Row>
        <Row term="Ward">{wardName(i.ward_id)}</Row>
        <Row term="Inspected by">
          {personLabel(i.inspected_by, principal.partyId)}
          {i.auth ? ` (${labelOf(AUTH_KIND, i.auth)})` : ""}
        </Row>
        <Row term="Recorded">
          {formatDateTime(i.recorded_at)}
          {i.offline ? " (captured offline in the Work app)" : ""}
        </Row>
        <Row term="Distance from the premises">{i.distance_from_premises_m == null ? "Not known" : metres(i.distance_from_premises_m)}</Row>
        <Row term="Checklist">
          {checklist ? checklist.label.en : i.checklist_key} (version {i.checklist_version})
        </Row>
        {i.certificate_id && <Row term="Certificate issued">{shortId(i.certificate_id)}</Row>}
        {i.notes && <Row term="Officer notes">{i.notes}</Row>}
      </dl>
      <table className="lcc-table" aria-label="Checklist answers">
        <thead>
          <tr>
            <th scope="col">Item</th>
            <th scope="col">Result</th>
            <th scope="col">Note</th>
          </tr>
        </thead>
        <tbody>
          {i.answers.map((a) => (
            <tr key={a.code}>
              <th scope="row">
                {itemLabel(a.code)}
                {critical(a.code) ? " (critical)" : ""}
                <ItemStandard item={itemOf(a.code)} />
              </th>
              <td>{labelOf(ANSWER_RESULT, a.result)}</td>
              <td>{a.note ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h4>Defects</h4>
      {!i.defects || i.defects.length === 0 ? (
        <p className="fer-muted">None.</p>
      ) : (
        <ul className="fer-plain-list" aria-label="Defects">
          {i.defects.map((d, n) => (
            <li key={n}>
              <StatusBadge status={statusOf(DEFECT_SEVERITY, d.severity)} /> {d.description}
              {d.fix_within_days ? ` – fix within ${d.fix_within_days} days` : ""}
              {d.code ? (
                <span className="fer-hint">
                  {" "}
                  ({itemLabel(d.code)}
                  <ItemStandard item={itemOf(d.code)} />)
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <h4>Photos</h4>
      <FileLinks ids={i.photo_file_ids} noun="Inspection photo" />
    </section>
  );
}

function InspectionsTab() {
  const wardName = useWardNames();
  const [outcome, setOutcome] = useState<InspectionOutcome | "">("");
  const [selected, setSelected] = useState<string | null>(null);
  const q = useApi(["inspections", outcome], (api) => endpoints.listInspections(api, { outcome: outcome || undefined }), { keepPrevious: true });
  const rows = q.data ?? [];
  return (
    <>
      <p className="fer-hint">Inspections are captured in the Smart City Work app (offline too); this list shows what has been recorded.</p>
      <div className="fer-toolbar">
        <label className="fer-field fer-field--inline">
          <span>Outcome</span>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value as InspectionOutcome | "")}>
            <option value="">All</option>
            {Object.entries(INSPECTION_OUTCOME).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} label="inspections" />
      {q.data && rows.length === 0 && <p className="fer-muted">No inspection matches.</p>}
      <div className={`fer-split${selected ? " fer-split--open" : ""}`}>
        <div className="fer-split__main">
          {rows.length > 0 && (
            <table className="lcc-table" aria-label={`Inspections (${rows.length})`}>
              <thead>
                <tr>
                  <th scope="col">Captured</th>
                  <th scope="col">Kind</th>
                  <th scope="col">Premises</th>
                  <th scope="col">Ward</th>
                  <th scope="col">Outcome</th>
                  <th scope="col">
                    <span className="lcc-sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <tr key={i.inspection_id} className={i.inspection_id === selected ? "fer-row--selected" : undefined}>
                    <th scope="row">{formatDateTime(i.captured_at)}</th>
                    <td>{labelOf(INSPECTION_KIND, i.kind)}</td>
                    <td>{shortId(i.premises_id)}</td>
                    <td>{wardName(i.ward_id)}</td>
                    <td>
                      <StatusBadge status={statusOf(INSPECTION_OUTCOME, i.outcome)} />
                    </td>
                    <td>
                      <Button variant="secondary" size="sm" onClick={() => setSelected(i.inspection_id)} aria-label={`Open the inspection of ${formatDateTime(i.captured_at)}`}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {selected && (
          <div className="fer-detailpane">
            <InspectionDetail key={selected} inspectionId={selected} onClose={() => setSelected(null)} />
          </div>
        )}
      </div>
    </>
  );
}

/** A held item's content in words (what was captured). */
export function heldSummary(h: HeldItem, names: ReturnType<typeof useConfigNames>): string {
  const it = h.item;
  if (it.kind === "inspection" && it.inspection) return `${labelOf(INSPECTION_KIND, it.inspection.kind)}: ${statusOf(INSPECTION_OUTCOME, it.inspection.outcome).label} for premises ${shortId(it.inspection.premises_id)}`;
  if (it.kind === "hydrant_register" && it.hydrant)
    return `New hydrant${it.hydrant.code ? ` ${it.hydrant.code}` : ""} (${statusOf(HYDRANT_CONDITION, it.hydrant.condition).label}) at ${it.hydrant.lon.toFixed(5)}, ${it.hydrant.lat.toFixed(5)}`;
  if (it.kind === "hydrant_survey" && it.survey) return `Survey: ${statusOf(HYDRANT_CONDITION, it.survey.condition).label}${it.survey.flow_lpm != null ? `, ${it.survey.flow_lpm} l/min` : ""}`;
  void names;
  return labelOf(HELD_KIND, it.kind);
}

/** A held inspection's answers and defects, each with the compulsory standard its item checks (decision 46). */
function HeldAnswers({ answers, defects }: { answers: Array<{ code: string; result: string; note?: string | null }>; defects: Array<{ code?: string | null; description: string }> }) {
  const names = useConfigNames();
  return (
    <>
      <ul className="fer-plain-list" aria-label="Answers of the held inspection">
        {answers.map((a) => {
          const item = checklistItem(names.staff, a.code);
          return (
            <li key={a.code}>
              {item?.label ?? a.code}: {labelOf(ANSWER_RESULT, a.result)}
              {a.note ? ` – ${a.note}` : ""}
              <ItemStandard item={item} />
            </li>
          );
        })}
      </ul>
      {defects.length > 0 && (
        <ul className="fer-plain-list" aria-label="Defects of the held inspection">
          {defects.map((d, n) => (
            <li key={n}>
              Defect: {d.description}
              {d.code ? <ItemStandard item={checklistItem(names.staff, d.code)} /> : null}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Why this person may not review a held item (UI only): its capturer, or a fire officer outside its ward. */
export function reviewBlock(h: HeldItem, principal: { roles: string[]; wardIds: string[]; partyId: string }): string | null {
  if (h.captured_by === principal.partyId) return "You captured this item, so another fire officer of the ward or the chief reviews it.";
  if (!principal.roles.includes(ROLE.chief) && !holdsWard(principal, h.ward_id)) return "This item is outside your wards.";
  return null;
}

function HeldTab() {
  const { principal } = useFireContext();
  const names = useConfigNames();
  const wardName = useWardNames();
  const [status, setStatus] = useState<"held_for_review" | "accepted" | "rejected">("held_for_review");
  const [review, setReview] = useState<HeldItem | null>(null);
  const q = useApi(["held", status], (api) => endpoints.listHeldItems(api, { status }), { keepPrevious: true });
  const rows = q.data ?? [];
  return (
    <>
      <p className="fer-hint">
        Field work captured offline outside the officer's wards at the time is held, never lost. Accepting records it as at its capture time
        (every other rule still applies); nobody reviews their own.
      </p>
      <div className="fer-toolbar">
        <label className="fer-field fer-field--inline">
          <span>Show</span>
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            {Object.entries(HELD_STATUS).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} label="held items" />
      {q.data && rows.length === 0 && <p className="fer-muted">No item.</p>}
      {rows.length > 0 && (
        <table className="lcc-table" aria-label={`Held field items (${rows.length})`}>
          <thead>
            <tr>
              <th scope="col">What</th>
              <th scope="col">Ward</th>
              <th scope="col">Captured</th>
              <th scope="col">Why held</th>
              <th scope="col">Status</th>
              <th scope="col">
                <span className="lcc-sr-only">Review</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => {
              const block = reviewBlock(h, principal);
              return (
                <tr key={h.held_id}>
                  <th scope="row">{heldSummary(h, names)}</th>
                  <td>{wardName(h.ward_id)}</td>
                  <td>
                    {formatDateTime(h.captured_at)} by {personLabel(h.captured_by, principal.partyId)} (synced {formatDateTime(h.synced_at)})
                  </td>
                  <td>{h.hold_reasons.map((r) => labelOf(HOLD_REASON, r)).join("; ")}</td>
                  <td>
                    <StatusBadge status={statusOf(HELD_STATUS, h.status)} />
                    {h.result && (
                      <>
                        {" "}
                        <StatusBadge status={statusOf(FIELD_OUTCOME, h.result.outcome)} />
                        {h.result.reason ? <span className="fer-hint"> {reasonMessage(h.result.reason)}</span> : null}
                      </>
                    )}
                    {h.reviewed_at && (
                      <span className="fer-hint">
                        {" "}
                        Reviewed {formatDateTime(h.reviewed_at)} by {personLabel(h.reviewed_by, principal.partyId)}: {h.review_reason}
                      </span>
                    )}
                  </td>
                  <td>
                    {h.status === "held_for_review" && can(principal, "reviewHeldItem") &&
                      (block ? (
                        <span className="fer-hint">{block}</span>
                      ) : (
                        <Button variant="secondary" size="sm" onClick={() => setReview(h)} aria-label={`Review: ${heldSummary(h, names)}`}>
                          Review
                        </Button>
                      ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {review && (
        <ActionDialog
          title="Review a held field item"
          intro={
            <>
              <Notice>{heldSummary(review, names)}</Notice>
              {review.item.kind === "inspection" && review.item.inspection && <HeldAnswers answers={review.item.inspection.answers ?? []} defects={review.item.inspection.defects ?? []} />}
            </>
          }
          fields={[
            { name: "decision", label: "Decision", kind: "select", required: true, options: [["accept", "Accept (record it as at its capture time)"], ["reject", "Reject"]] },
            { name: "reason", label: "Reason (at least 3 characters)", kind: "textarea", required: true, maxLength: 1000 },
          ]}
          submitLabel="Record the review"
          build={(v) => {
            const e = reasonError(v.reason ?? "");
            return e ? { error: e } : { body: { decision: v.decision, reason: v.reason.trim() } };
          }}
          write={(api, body) => endpoints.reviewHeldItem(api, review.held_id, body as never)}
          onClose={() => setReview(null)}
        />
      )}
    </>
  );
}

type Tab = "inspections" | "held";

/** Inspections recorded (checklists, defects, photos) and the review of field items held outside the officer's wards. */
export function InspectionsScreen() {
  const { principal } = useFireContext();
  const tabs = [
    ...(can(principal, "viewInspections") ? [{ key: "inspections" as const, label: "Inspections" }] : []),
    ...(can(principal, "viewHeldItems") ? [{ key: "held" as const, label: "Held field items" }] : []),
  ];
  const [tab, setTab] = useState<Tab>(tabs[0]?.key ?? "inspections");
  const current = tabs.some((t) => t.key === tab) ? tab : (tabs[0]?.key ?? "inspections");
  return (
    <div className="fer-screen">
      <ScreenHeader title="Inspections" />
      <Tabs label="Inspections" tabs={tabs} current={current} onChange={setTab}>
        {current === "inspections" ? <InspectionsTab /> : <HeldTab />}
      </Tabs>
    </div>
  );
}
